import type { AgentNodeData, CanvasEdge, CanvasNode, CanvasNodeType, GenerateVideoNodeData } from './types';
import { modelSupportsVideoReference, modelSupportsAudioReference } from './modelCache';

const NODE_TYPE_LABELS: Record<CanvasNodeType, string> = {
  text: '文本节点',
  image: '图片节点',
  video: '视频节点',
  audio: '声音节点',
  generate_image: '生图节点',
  generate_video: '生视频节点',
  image_group: '图片组',
  video_group: '视频组',
  llm: 'LLM 节点',
  agent: '智能体节点',
  style_card: '风格卡片',
  group: '分组',
};

/**
 * 手动连线允许表（源类型 → 可作为目标的类型）。
 * 用户手势（拖线落点 / 参考点选 / 创建并连接菜单）一律以此表为准。
 */
const ALLOWED_TARGETS: Record<CanvasNodeType, CanvasNodeType[]> = {
  text: ['llm', 'generate_image', 'generate_video', 'agent'],
  // image → image：派生边（裁剪/抽帧/切割的产物节点），仅表达血缘，产物图片节点不消费上游；
  //   用户手动画 image → image 会被手势层拦截并转换为「合并为图片组」，不落边（见 NodeWrapper）。
  // image → image_group：手势层拦截为「加入该组」（图片被吸收为组槽位，不落边）。
  // image → llm：反推提示词，LLM 节点检测到上游图片时自动切 vision 模式。
  // 聚合只取 generate 节点的直接上游，不受影响。
  image: ['generate_image', 'generate_video', 'image', 'image_group', 'agent', 'llm'],
  // video → generate_video：参考视频输入，仅当目标生视频节点当前「模型+模式」
  //   支持参考视频时可连（canConnectNodes 动态判定）。
  // video → video：手势层拦截为「合并为视频组」，不落边。
  // video → video_group：手势层拦截为「加入该视频组」，不落边。
  // 视频节点不再有任何图片类下游（含抽帧派生边）：任何节点不得消费视频。
  video: ['generate_video', 'video', 'video_group'],
  // audio → generate_video：参考音频输入，仅当目标生视频节点当前「模型+模式」
  //   支持参考音频时可连（canConnectNodes 动态判定，与 video → generate_video 同口径）。
  audio: ['generate_video'],
  generate_image: ['image'],
  generate_video: ['video'],
  // image_group → llm：整组图片批量反推提示词。
  // image_group → image_group：手势层拦截为「组合并」（源组并入目标组后消失，不落边）。
  image_group: ['generate_image', 'generate_video', 'image_group', 'agent', 'llm'],
  // video_group → generate_video：整组视频作为参考视频上游（动态能力判定同 video）。
  // video_group → video_group：手势层拦截为「组合并」，不落边；也不消费任何其他节点。
  video_group: ['video_group', 'generate_video'],
  llm: ['llm', 'generate_image', 'generate_video', 'agent'],
  // agent 没有任何手动连线目标：智能体自动派生生图工作卡并连血缘边
  // （agent → 工作卡 → 结果组两跳），结果组关联靠自身 groupNodeId 数据字段；
  // 手动 agent → 任何节点都是死边（此前 agent → 生图已禁，agent → 图片组同理）
  agent: [],
  // 风格卡片输出提示词文本，可连生图/生视频/智能体（与 text 节点同口径）
  style_card: ['generate_image', 'generate_video', 'agent'],
  group: [],
};

/**
 * 系统血缘边：仅由程序化图操作产生与消费，用户不可手动绘制
 * （手动入口 addEdge 只认 ALLOWED_TARGETS）。登记形态：
 *  - agent → generate_image：智能体派生的工作卡（agentGenerator）
 *  - generate_image → image_group：工作卡 → 结果组；多图聚合时由
 *    generate_image → image 血缘边改接而来；extractImageFromGroup 的
 *    「专属上游判定」依赖它
 *  - video → image_group / video → image / video → generate_image /
 *    video → agent：仅兼容规则收紧前的存量边（粘贴/聚合改接恢复不丢）；
 *    新边不再产生（视频节点下游只剩支持参考视频的生视频节点）
 *  - agent → image_group：仅兼容存量手动边（该边无任何消费方，新边已禁）
 * 供 addNodes / applyGraphPatch 的防御性校验放行（手动合法 ∪ 系统血缘）。
 */
const SYSTEM_LINEAGE_EDGES: ReadonlySet<string> = new Set([
  'agent->generate_image',
  'agent->image_group',
  'generate_image->image_group',
  'video->image_group',
  'video->image',
  'video->generate_image',
  'video->agent',
  // video_group 吸收视频节点时，原 generate_video → video 血缘边转接到组
  'generate_video->video_group',
]);

/** 程序化入口防御校验用：是否为登记在案的系统血缘边 */
export function isSystemLineageEdge(
  sourceType: CanvasNodeType,
  targetType: CanvasNodeType
): boolean {
  return SYSTEM_LINEAGE_EDGES.has(`${sourceType}->${targetType}`);
}

/** 程序化入口防御校验用：手动合法边或系统血缘边均放行 */
export function isProgrammaticEdgeAllowed(
  sourceType: CanvasNodeType,
  targetType: CanvasNodeType
): boolean {
  return canConnectNodeTypes(sourceType, targetType) || isSystemLineageEdge(sourceType, targetType);
}

export function getNodeTypeLabel(type: CanvasNodeType): string {
  return NODE_TYPE_LABELS[type];
}

export function getAllowedTargetTypes(sourceType: CanvasNodeType): CanvasNodeType[] {
  return ALLOWED_TARGETS[sourceType] ?? [];
}

export function canConnectNodeTypes(sourceType: CanvasNodeType, targetType: CanvasNodeType): boolean {
  return getAllowedTargetTypes(sourceType).includes(targetType);
}

export function getInvalidConnectionMessage(sourceType: CanvasNodeType, targetType: CanvasNodeType): string {
  return `${getNodeTypeLabel(sourceType)}不能连接到${getNodeTypeLabel(targetType)}`;
}

/**
 * 节点级连线判定（用户手势入口统一走这里）：类型表合法 + 动态能力例外。
 * video → generate_video 是画布视频消费的唯一例外，要求目标生视频节点
 * 当前「模型+模式」开启参考视频能力；模型未选/未加载视为不支持。
 */
export function canConnectNodes(
  sourceNode: CanvasNode,
  targetNode: CanvasNode
): { ok: boolean; reason?: string } {
  if (!canConnectNodeTypes(sourceNode.type, targetNode.type)) {
    return { ok: false, reason: getInvalidConnectionMessage(sourceNode.type, targetNode.type) };
  }
  if (
    (sourceNode.type === 'video' || sourceNode.type === 'video_group') &&
    targetNode.type === 'generate_video'
  ) {
    const data = targetNode.data as GenerateVideoNodeData;
    const mode = (data.mode || 'text2video') as Parameters<typeof modelSupportsVideoReference>[1];
    if (!data.modelId) {
      return { ok: false, reason: '请先在该生视频节点选择支持参考视频的模型，再连接视频节点' };
    }
    if (!modelSupportsVideoReference(data.modelId, mode)) {
      return { ok: false, reason: '该生视频节点当前模型/模式不支持参考视频，无法连接视频节点' };
    }
  }
  // 参考音频：声音节点只能连支持参考音频的生视频节点（与参考视频同口径）
  if (sourceNode.type === 'audio' && targetNode.type === 'generate_video') {
    const data = targetNode.data as GenerateVideoNodeData;
    const mode = (data.mode || 'text2video') as Parameters<typeof modelSupportsAudioReference>[1];
    if (!data.modelId) {
      return { ok: false, reason: '请先在该生视频节点选择支持参考音频的模型，再连接声音节点' };
    }
    if (!modelSupportsAudioReference(data.modelId, mode)) {
      return { ok: false, reason: '该生视频节点当前模型/模式不支持参考音频，无法连接声音节点' };
    }
  }
  return { ok: true };
}

/**
 * 智能体批次血缘边判定（删除保护用）：
 *  - agent → image_group：新形态批次的唯一血缘边（agent 直连结果组）；
 *  - agent → generate_image 与 generate_image → image_group（source 为某
 *    agent 的 childNodeIds 成员/工作卡）：存量「工作卡」形态批次的血缘链，
 *    按节点归属识别而非比对 groupNodeId，兼容组合并后改接的情况。
 * 这些边仅由程序化图操作产生、手动无法重建（agent / image_group 均不是
 * 手动合法目标），冻结语义下用户手势不可删除；
 * boardStore.removeEdges 以此过滤并回告（见 agentUnit 冻结单元）。
 */
export function isAgentLineageEdge(edge: CanvasEdge, nodes: CanvasNode[]): boolean {
  const source = nodes.find((n) => n.id === edge.sourceNodeId);
  const target = nodes.find((n) => n.id === edge.targetNodeId);
  if (!source || !target) return false;
  if (source.type === 'agent' && (target.type === 'image_group' || target.type === 'generate_image')) {
    return true;
  }
  if (source.type === 'generate_image' && target.type === 'image_group') {
    return nodes.some(
      (n) =>
        n.type === 'agent' &&
        ((n.data as AgentNodeData).childNodeIds ?? []).includes(source.id)
    );
  }
  return false;
}
